// /src/lib/kbAdminClient.ts
import { supabase } from "@/lib/supabase";
import { readWorkspaceOrgIdFromStorage } from "@/lib/workspaceOrgStorage";

type ApiOk<T> = { ok: true } & T;
type ApiErr = { ok: false; error: string; detail?: any };

export function getAdminToken() {
  return localStorage.getItem("bb_admin_token") || "";
}
export function setAdminToken(token: string) {
  localStorage.setItem("bb_admin_token", token);
}
export function clearAdminToken() {
  localStorage.removeItem("bb_admin_token");
}

type AdminSessionInfo = {
  ver: number | null;
  exp: number | null;          // unix seconds
  expIso: string | null;       // human readable
  secondsLeft: number | null;
};

function base64UrlToJson(input: string) {
  const b64 = input.replace(/-/g, "+").replace(/_/g, "/");
  const pad = b64.length % 4 ? "=".repeat(4 - (b64.length % 4)) : "";
  const jsonStr = atob(b64 + pad);
  return JSON.parse(jsonStr);
}

export async function adminMe() {
  return adminPost<{ ok: true; ver: number; tokenVer: number | null; exp: number | null; iat: number | null; admin: boolean }>(
    "/api/kb/admin/me",
    { method: "GET" }
  );
}

export function getAdminSessionInfo(): AdminSessionInfo | null {
  const token = getAdminToken();
  if (!token) return null;

  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;

    const payload = base64UrlToJson(parts[1]) as any;
    const ver = typeof payload?.ver === "number" ? payload.ver : null;
    const exp = typeof payload?.exp === "number" ? payload.exp : null;

    const nowSec = Math.floor(Date.now() / 1000);
    const secondsLeft = exp ? Math.max(0, exp - nowSec) : null;
    const expIso = exp ? new Date(exp * 1000).toLocaleString() : null;

    return { ver, exp, expIso, secondsLeft };
  } catch {
    return null;
  }
}

async function postJson(path: string, body: unknown, token?: string, workspaceOrgId?: string | null) {
  const r = await fetch(path, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(workspaceOrgId ? { "X-Workspace-Organization-Id": workspaceOrgId } : {}),
    },
    body: JSON.stringify(body),
  });

  const text = await r.text();
  let json: any = null;
  try { json = JSON.parse(text); } catch { /* intentional */ }

  return { status: r.status, ok: r.ok, json, text };
}

async function getRequestToken() {
  const adminToken = getAdminToken();
  if (adminToken) return { token: adminToken, source: "kb-admin" as const };

  if (!supabase) return { token: "", source: "none" as const };
  const { data } = await supabase.auth.getSession();
  const accessToken = data.session?.access_token || "";
  return { token: accessToken, source: accessToken ? ("supabase" as const) : ("none" as const) };
}

export async function adminLogin(password: string): Promise<ApiOk<{ token: string; ver: number }> | ApiErr> {
  const r = await postJson("/api/kb/admin/login", { password });
  if (!r.ok) return { ok: false, error: r.json?.error || r.text || `HTTP ${r.status}` };
  if (!r.json?.token) return { ok: false, error: "Login succeeded but no token returned" };
  setAdminToken(r.json.token);
  return { ok: true, token: r.json.token, ver: r.json.ver };
}

export type AdminPostErr = {
  ok: false;
  status: number;
  error: string;
  /** Machine-readable error from API (e.g. POLICY_OVERWRITE_CONFIRM). */
  code?: string;
  /** Optional structured details for confirm/review flows. */
  conflicts?: Array<{ slug?: string; title?: string; overlap_score?: number; shared_tag_count?: number }>;
  /** Raw JSON payload from API error response for custom handlers. */
  detail?: unknown;
};

export async function adminPost<T>(
  path: string,
  body: unknown
): Promise<{ ok: true; status: number; data: T } | AdminPostErr> {
  const auth = await getRequestToken();
  const workspaceOrgId = auth.source === "supabase" ? readWorkspaceOrgIdFromStorage() : null;
  const r = await postJson(path, body, auth.token || undefined, workspaceOrgId);

  if (!r.ok) {
    const msg = r.json?.error || r.text || `HTTP ${r.status}` || "KB access expired. Please sign in again.";
    if (r.status === 401 && auth.source === "kb-admin") clearAdminToken();
    const code = typeof r.json?.code === "string" ? r.json.code : undefined;
    const conflicts = Array.isArray(r.json?.conflicts) ? r.json.conflicts : undefined;
    return { ok: false, status: r.status, error: msg, code, conflicts, detail: r.json };
  }
  return { ok: true, status: r.status, data: r.json as T };
}